import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { batchesAPI } from '../../services/batches';
import { getCountries } from '../../data/locationData';
import { useAuth } from '../../contexts/AuthContext';
import { getViewerTimezone } from '../../utils/dateTimeUtils';
import { AGE_GROUPS } from '../../constants/ageGroups';
import { getTimezoneAbbreviation, getTimezoneOptionLabel, normalizeTimezoneForFormSelect } from '../../utils/timezoneUtils';
import TimezoneSelect from '../../components/TimezoneSelect';
import {
  formatAgeGroupCanAdd,
  formatBatchDaysFromTimeLines,
  formatScheduleTimeLines,
  formatStudentAgesDisplay,
} from '../../utils/batchScheduleTimeDisplay';

const dayOptions = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
];

const ageGroups = AGE_GROUPS;
const countryOptions = getCountries();
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const initialFilters = {
  search: '',
  teacher_country: '',
  student_country: '',
  age_group: '',
  days: [],
  time_from: '',
  time_to: '',
  teacher_id: '',
  batch_type: '',
  status: '',
  is_inclusive: '',
  has_capacity: '',
  sort_by: 'start_date',
  sort_dir: 'asc',
};

function formatBatchStartDate(ymd) {
  if (!ymd) return '—';
  const s = String(ymd).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return `${d} ${MONTH_NAMES[m - 1] || m}`;
}

function getTeacherLabel(teacher) {
  const name = teacher.name || teacher.full_name || [teacher.first_name, teacher.last_name].filter(Boolean).join(' ').trim();
  return name || teacher.email || teacher.id;
}

export default function BatchEnrollmentOptions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userTimezone = user?.timezone || getViewerTimezone(user?.timezone);
  const [displayTimezone, setDisplayTimezone] = useState(() =>
    normalizeTimezoneForFormSelect(userTimezone)
  );
  const [timezoneTouched, setTimezoneTouched] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    if (timezoneTouched || !user?.timezone) return;
    setDisplayTimezone(normalizeTimezoneForFormSelect(user.timezone));
  }, [user?.timezone, timezoneTouched]);

  const teacherFilterParams = useMemo(
    () => ({
      search: filters.search || undefined,
      teacher_country: filters.teacher_country || undefined,
      student_country: filters.student_country || undefined,
      age_group: filters.age_group || undefined,
      days: filters.days.length ? filters.days.join(',') : undefined,
      time_from: filters.time_from || undefined,
      time_to: filters.time_to || undefined,
      batch_type: filters.batch_type || undefined,
      status: filters.status || undefined,
      is_inclusive: filters.is_inclusive !== '' ? filters.is_inclusive : undefined,
      has_capacity: filters.has_capacity !== '' ? filters.has_capacity : undefined,
      enrollment_window: true,
    }),
    [filters]
  );

  const { data: teachersResponse } = useQuery(
    ['teachers-with-batch-counts-enrollment', teacherFilterParams],
    () => batchesAPI.getTeachersWithCounts(teacherFilterParams),
    { staleTime: 2 * 60 * 1000 }
  );

  const teachers = teachersResponse?.data?.data || [];

  const apiParams = useMemo(
    () => ({
      page,
      limit,
      search: filters.search || undefined,
      teacher_country: filters.teacher_country || undefined,
      student_country: filters.student_country || undefined,
      age_group: filters.age_group || undefined,
      days: filters.days.length ? filters.days.join(',') : undefined,
      time_from: filters.time_from || undefined,
      time_to: filters.time_to || undefined,
      teacher_id: filters.teacher_id || undefined,
      batch_type: filters.batch_type || undefined,
      status: filters.status || undefined,
      is_inclusive: filters.is_inclusive !== '' ? filters.is_inclusive : undefined,
      has_capacity: filters.has_capacity !== '' ? filters.has_capacity : undefined,
      sort_by: filters.sort_by || undefined,
      sort_dir: filters.sort_dir || undefined,
    }),
    [page, limit, filters]
  );

  const { data, isLoading, error, refetch } = useQuery(
    ['batchEnrollmentOptions', apiParams],
    () => batchesAPI.getEnrollmentOptions(apiParams),
    { keepPreviousData: true, staleTime: 2 * 60 * 1000 }
  );

  const batches = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, pages: 0, total: 0 };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ ...initialFilters });
    setPage(1);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Batch Options
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Batches that started in the last 30 days or are scheduled to start in the future — for matching new students.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => refetch()} disabled={isLoading}>
            Refresh
          </Button>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search batch"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by batch name"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Teacher Country</InputLabel>
                <Select
                  label="Teacher Country"
                  value={filters.teacher_country}
                  onChange={(e) => handleFilterChange('teacher_country', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {countryOptions.map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Student Country</InputLabel>
                <Select
                  label="Student Country"
                  value={filters.student_country}
                  onChange={(e) => handleFilterChange('student_country', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {countryOptions.map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Age Group</InputLabel>
                <Select
                  label="Age Group"
                  value={filters.age_group}
                  onChange={(e) => handleFilterChange('age_group', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {ageGroups.map((group) => (
                    <MenuItem key={group} value={group}>{group}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Days</InputLabel>
                <Select
                  multiple
                  label="Days"
                  value={filters.days}
                  onChange={(e) => handleFilterChange('days', e.target.value)}
                  input={<OutlinedInput label="Days" />}
                  renderValue={(selected) =>
                    selected.map((val) => dayOptions.find((d) => d.value === val)?.label || val).join(', ')
                  }
                >
                  {dayOptions.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      <Checkbox checked={filters.days.indexOf(day.value) > -1} />
                      <ListItemText primary={day.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Teacher</InputLabel>
                <Select
                  label="Teacher"
                  value={filters.teacher_id}
                  onChange={(e) => handleFilterChange('teacher_id', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <span>{getTeacherLabel(teacher)}</span>
                        <Chip
                          label={`${teacher.batch_count || 0}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TimezoneSelect
                label="Show times in"
                size="small"
                value={displayTimezone}
                onChange={(next) => {
                  setTimezoneTouched(true);
                  setDisplayTimezone(next);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Start Time (teacher tz)"
                type="time"
                value={filters.time_from}
                onChange={(e) => handleFilterChange('time_from', e.target.value)}
                inputProps={{ step: 300 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="End Time (teacher tz)"
                type="time"
                value={filters.time_to}
                onChange={(e) => handleFilterChange('time_to', e.target.value)}
                inputProps={{ step: 300 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Has Capacity</InputLabel>
                <Select
                  label="Has Capacity"
                  value={filters.has_capacity}
                  onChange={(e) => handleFilterChange('has_capacity', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  <MenuItem value="true">Available seats</MenuItem>
                  <MenuItem value="false">Full</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="to_start">To start</MenuItem>
                  <MenuItem value="in_progress">In progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="all">All (incl. inactive)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Batch Type</InputLabel>
                <Select
                  label="Batch Type"
                  value={filters.batch_type}
                  onChange={(e) => handleFilterChange('batch_type', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  <MenuItem value="group">Group</MenuItem>
                  <MenuItem value="one_on_one">One-on-One</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort</InputLabel>
                <Select
                  label="Sort"
                  value={`${filters.sort_by}:${filters.sort_dir}`}
                  onChange={(e) => {
                    const [by, dir] = String(e.target.value || '').split(':');
                    setFilters((prev) => ({
                      ...prev,
                      sort_by: by || 'start_date',
                      sort_dir: dir || 'asc',
                    }));
                    setPage(1);
                  }}
                >
                  <MenuItem value="start_date:asc">Start date (earliest)</MenuItem>
                  <MenuItem value="start_date:desc">Start date (latest)</MenuItem>
                  <MenuItem value="name:asc">Name (A → Z)</MenuItem>
                  <MenuItem value="name:desc">Name (Z → A)</MenuItem>
                  <MenuItem value="created_at:desc">Newest</MenuItem>
                  <MenuItem value="created_at:asc">Oldest</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" gap={1}>
                <Button variant="outlined" onClick={handleClearFilters}>Clear</Button>
                <Button variant="contained" onClick={() => refetch()}>Apply</Button>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Times and days are shown in {getTimezoneAbbreviation(displayTimezone)}
            {getTimezoneAbbreviation(displayTimezone) !== getTimezoneAbbreviation(userTimezone)
              ? ` (your timezone is ${getTimezoneAbbreviation(userTimezone)})`
              : ''}
            . Day and start/end time filters still match the teacher's wall-clock schedule.
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.response?.data?.message || error.message || 'Failed to load batch options'}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Batch Start Date</strong></TableCell>
                <TableCell><strong>Group No</strong></TableCell>
                <TableCell><strong>Current Kids Age</strong></TableCell>
                <TableCell><strong>Can Added</strong></TableCell>
                <TableCell><strong>Days</strong></TableCell>
                <TableCell><strong>Time</strong></TableCell>
                <TableCell align="center"><strong>Seats Available</strong></TableCell>
                <TableCell><strong>Teacher</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center"><CircularProgress size={28} /></TableCell>
                </TableRow>
              ) : batches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">No batches match your filters.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                batches.map((batch) => {
                  const timeLines = formatScheduleTimeLines({
                    schedules: batch.schedules,
                    anchorDate: batch.start_date,
                    teacherTimezone: batch.teacher_timezone,
                    viewerTimezone: displayTimezone,
                  });
                  const daysDisplay = formatBatchDaysFromTimeLines(timeLines);

                  return (
                    <TableRow key={batch.id} hover>
                      <TableCell>{formatBatchStartDate(batch.start_date)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                          onClick={() => navigate(`/batches/${batch.id}`)}
                        >
                          {batch.name}
                        </Typography>
                        {(batch.country || batch.timezone) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {[batch.country, batch.timezone ? getTimezoneOptionLabel(batch.timezone) : ''].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                        {batch.is_inclusive && (
                          <Chip label="Inclusive" size="small" color="secondary" variant="outlined" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>
                        <Typography variant="body2">{formatStudentAgesDisplay(batch.student_ages)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatAgeGroupCanAdd(batch.age_group)}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography variant="body2">{daysDisplay}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        {timeLines.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        ) : (
                          timeLines.map((t, idx) => (
                            <Typography key={`${batch.id}-${t.line}-${idx}`} variant="body2" sx={{ mb: 0.5 }}>
                              {t.line}
                            </Typography>
                          ))
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={batch.seats_available}
                          size="small"
                          color={batch.seats_available > 0 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{batch.teacher_name || '—'}</Typography>
                        {batch.teacher_country && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {batch.teacher_country}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination.pages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={pagination.pages}
              page={page}
              onChange={(_e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Showing {batches.length} of {pagination.total} batches
        </Typography>
      </Box>
    </Container>
  );
}
